import pubsub from '../pubsub';
/**
 * @description
 * Exposes utility APIs in the Constellation Core that publish and subscribe events
 */
class PubSubUtils {
  /**
   * Notifies the subscription item(which can be uniquely identified by subscriptionItemName)
   * whenever a specific type of event occurs
   *
   * @example <caption>Example for subscribe()</caption>
   * Example usage - PCore.getPubSubUtils().subscribe("showCancelAlert",()=>{},"createStageCancelAlert",false,"app/primary_1");
   * // notifies the createStageCancelAlert function whenever an event of type showCancelAlert occurs
   * @param eventType - The type of event
   * @param subscriptionItem - The callback function that is invoked when a specific type of event occurs.
   * @param subscriptionItemName - A unique ID or name assigned by the user to identify the subscription item.
   * @param subscribeOnce - The flag that determines if the subscription item should be notified only once.
   * @param contextName - The name of the context containing the event whose subscription item must be notified. eq., app/primary_1
   * @function
   */

  static subscribe(
    eventType: string,
    subscriptionItem: Function,
    subscriptionItemName?: string,
    subscribeOnce = false,
    contextName = 'app'
  ) {
    pubsub.subscribe(eventType, subscriptionItemName, subscriptionItem, contextName, subscribeOnce);
  }

  /**
   * Notifies the subscription item(which can be uniquely identified by subscriptionItemName) only once
   * whenever a specific type of event occurs
   *
   * @example <caption>Example for subscribeOnce()</caption>
   * Example usage - PCore.getPubSubUtils().subscribeOnce("showCancelAlert",()=>{},"createStageCancelAlert");
   * // Notifies the createStageCancelAlert function only once when an event of type showCancelAlert occurs
   * @param eventType - The type of event
   * @param subscriptionItem - The callback function that is invoked only once when a specific type of event occurs.
   * @param subscriptionItemName - The unique name or ID used to identify the subscription item.
   * @function
   */
  static subscribeOnce(eventType: string, subscriptionItem: Function, subscriptionItemName: string) {
    this.subscribe(eventType, subscriptionItem, subscriptionItemName, true);
  }

  /**
   * Invokes all items subscribed to a specific type of event and
   * passes the specified payload to the subscribed items
   *
   * @example <caption>Example for publish</caption>
   * Example usage - PCore.getPubSubUtils().publish(eventType,payload);
   * // invokes all items subscribed to the event type and passes the payload to the items
   *
   * @param eventType - The type of event whose subscribed events must be invoked.
   * @param payload - The information that must be passed to the subscribed items.
   * @function
   */
  static publish(eventType: string, payload?: any) {
    pubsub.publish(eventType, payload);
  }

  /**
   * Unsubscribes a subscription item belonging to a specific type of event.
   *
   * @example <caption>Example for unsubscribe()</caption>
   * Example usage - PCore.getPubSubUtils().unsubscribe("showCancelAlert","createStageCancelAlert","app/primary_1");
   * // Unsubscribes the createStageCancelAlert function belonging to the event of type showCancelAlert.
   * @param eventType - The type of event
   * @param subscriptionItemName - The unique ID or name used to identify the subscription item.
   * @param contextName - The name of the context containing the event whose subscription item must be unsubscribed. eq., app/primary_1
   * @function
   */
  static unsubscribe(eventType: string, subscriptionItemName: string, contextName = 'app') {
    pubsub.unsubscribe(eventType, subscriptionItemName, contextName);
  }

  /**
   * Unsubscribes all subscription items belonging to a specific type of event.
   *
   * @private
   * @param eventType - The type of event
   */
  static unsubscribeAllWithEventType(eventType: string) {
    pubsub.unsubscribeAllWithEventType(eventType);
  }

  /**
   * Deletes the subscription items of all events under the given context.
   *
   * @example <caption>Example for cleanContextSubscribers()</caption>
   * Example usage - PCore.getPubSubUtils().cleanContextSubscribers("app/primary_1");
   * // Clears all the subscription-item of all the event which fall under app/primary_1
   * @param contextName - The name of the context containing the events whose subscription items must be deleted. eq., app/primary_1
   * @function
   */
  static cleanContextSubscribers(contextName: string) {
    pubsub.clearContext(contextName);
  }
}

export default PubSubUtils;
